import { useRouter } from 'next/router';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { InviteCodeState } from './types';

export const inviteCodeQueryString = 'invite_code';
export const InviteCodeContext = createContext<InviteCodeState>(undefined!);

export function InviteCodeProvider({
    children,
}: {
    children: ReactNode | ReactNode[];
}) {
    const { query, replace, pathname, isReady } = useRouter();
    const { [inviteCodeQueryString]: queryCode } = query;

    const [stateCode, setStateCode] = useState<string>('');

    const inviteCode = useMemo(
        () => (stateCode ? stateCode : queryCode ? (queryCode as string) : ''),
        [queryCode, stateCode]
    );

    const setInviteCode = useCallback(
        async (code: string, willSetQuery = false) => {
            setStateCode(code);
            if (willSetQuery) {
                await replace(
                    {
                        pathname,
                        query: { ...query, [inviteCodeQueryString]: code },
                    },
                    undefined,
                    { shallow: true }
                );
            }
        },
        [pathname, query, replace]
    );

    const removeInviteCode = useCallback(async () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [inviteCodeQueryString]: _removed, ...rest } = query;
        setStateCode('');
        await replace({ pathname, query: rest }, undefined, { shallow: true });
    }, [pathname, query, replace]);

    return (
        <InviteCodeContext.Provider
            value={{
                isReady,
                inviteCode,
                hasInviteCode: !!inviteCode,
                isInviteCodeFromQuery: !!inviteCode && queryCode === inviteCode,
                setInviteCode,
                removeInviteCode,
            }}
        >
            {children}
        </InviteCodeContext.Provider>
    );
}
